import { Window } from '@execonline-inc/browser-window.private';
import React, { Component, ReactNode } from 'react';
import { T } from '../../../../Translations';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // TODO: Log to error tracking service (Honeybadger, Sentry, etc.)
    // Avoid console.error in production - should use proper error tracking
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-muted/10">
          <div className="max-w-md p-8 bg-card rounded-lg shadow-lg border border-border">
            <div className="flex items-center gap-3 mb-4">
              <svg
                className="w-8 h-8 text-destructive"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className="text-xl" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                <T kind="Something went wrong" />
              </h2>
            </div>
            <p className="text-muted-foreground mb-6">
              We encountered an error while loading the Add Participant page. Please try refreshing
              the page or contact support if the issue persists.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
              >
                Refresh Page
              </button>
              <button
                onClick={() => Window.redirect('/people/participants')}
                className="px-4 py-2 bg-card text-foreground border border-border rounded-md hover:bg-muted/50 transition-colors"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
              >
                <T kind="Return to Participants" />
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4">
                <summary className="text-sm text-muted-foreground cursor-pointer">
                  Error details (dev only)
                </summary>
                <pre className="mt-2 p-3 bg-muted/50 rounded text-xs overflow-auto">
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
